import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-client';
import type { InventoryValidation, MeasurementUnit } from '@/types/entities';
import { toast } from 'sonner';
import { isAssistantLoggedIn } from '@/lib/permissions';

// Time filter options
export type TimeFilter = 'today' | 'week' | 'month' | 'all';

// Filters for querying validations
export interface ValidationFilters {
  ingredientId?: string | undefined;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  timeFilter?: TimeFilter | undefined;
  createdBy?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

// Input for validating stock
export interface ValidateStockInput {
  ingredientId: string;
  actualQuantity: number;
  validationDate?: Date;
  reason?: string | null | undefined;
  notes?: string | null | undefined;
}

// Helper to compute date range from time filter
function getDateRangeFromTimeFilter(timeFilter: TimeFilter): { startDate?: Date; endDate?: Date } {
  const now = new Date();
  now.setHours(23, 59, 59, 999);

  switch (timeFilter) {
    case 'today': {
      const startDate = new Date(now);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'week': {
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'month': {
      const startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
      startDate.setHours(0, 0, 0, 0);
      return { startDate, endDate: now };
    }
    case 'all':
    default:
      return {};
  }
}

// Response interface with pagination metadata
export interface ValidationsResponse {
  data: InventoryValidation[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Fetch inventory validations for a restaurant with optional filters
 */
export function useInventoryValidations(
  restaurantId: string,
  filters?: ValidationFilters
) {
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 10;

  // Compute date range from time filter or use explicit dates
  const dateRange = filters?.timeFilter
    ? getDateRangeFromTimeFilter(filters.timeFilter)
    : { startDate: filters?.startDate, endDate: filters?.endDate };

  // Serialize filters for stable query key
  const queryKey = useMemo(
    () => [
      'validations',
      restaurantId,
      filters?.ingredientId,
      filters?.timeFilter,
      filters?.createdBy,
      dateRange.startDate?.toISOString(),
      dateRange.endDate?.toISOString(),
      page,
      pageSize,
    ],
    [restaurantId, filters?.ingredientId, filters?.timeFilter, filters?.createdBy, dateRange.startDate?.getTime(), dateRange.endDate?.getTime(), page, pageSize]
  );

  return useQuery({
    queryKey,
    queryFn: async (): Promise<ValidationsResponse> => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function
        const { data, error } = await supabase.rpc('assistant_get_inventory_validations', {
          p_restaurant_id: restaurantId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view inventory validations');
            return { data: [], totalCount: 0, page, pageSize, totalPages: 0 };
          }
          throw error;
        }

        // Fetch ingredients for the validations using RPC (assistants can't query tables directly)
        const { data: ingredients, error: ingredientsError } = await supabase.rpc('assistant_get_ingredients', {
          p_restaurant_id: restaurantId,
        });
        if (ingredientsError) {
          console.warn('Failed to fetch ingredients for assistant:', ingredientsError);
        }
        const ingredientsMap = new Map((ingredients || []).map((i: any) => [i.id, i]));

        // Map to InventoryValidation and apply client-side filtering
        let validations = (data || []).map((v: any): InventoryValidation & { createdByDisplay?: string; updatedByDisplay?: string } => {
          const ingredient = ingredientsMap.get(v.ingredient_id);
          const validation: InventoryValidation & { createdByDisplay?: string; updatedByDisplay?: string } = {
            id: v.id,
            restaurantId: v.restaurant_id,
            ingredientId: v.ingredient_id,
            inventoryId: v.inventory_id,
            validationDate: v.validation_date,
            calculatedQuantity: v.calculated_quantity,
            actualQuantity: v.actual_quantity,
            difference: v.difference ?? 0,
            unit: v.unit as MeasurementUnit,
            averageCostAtValidation: v.average_cost_at_validation,
            costVariance: v.cost_variance ?? 0,
            reason: v.reason ?? null,
            notes: v.notes ?? null,
            validatedBy: v.validated_by ?? null,
            createdAt: v.created_at || '',
            deletedAt: v.deleted_at || null,
            updatedAt: v.updated_at || null,
          };
          // Audit display fields from RPC
          if (v.created_by_display) validation.createdByDisplay = v.created_by_display;
          if (v.updated_by_display) validation.updatedByDisplay = v.updated_by_display;
          if (ingredient) {
            validation.ingredient = {
              id: ingredient.id,
              restaurantId: ingredient.restaurant_id,
              nameAr: ingredient.name_ar,
              nameFr: ingredient.name_fr,
              emoji: ingredient.emoji || '',
              createdAt: ingredient.created_at || '',
              updatedAt: ingredient.updated_at || '',
            };
          }
          return validation;
        });

        // Apply client-side filtering
        if (filters?.ingredientId) {
          validations = validations.filter(v => v.ingredientId === filters.ingredientId);
        }
        if (dateRange.startDate) {
          validations = validations.filter(v => new Date(v.validationDate) >= dateRange.startDate!);
        }
        if (dateRange.endDate) {
          validations = validations.filter(v => new Date(v.validationDate) <= dateRange.endDate!);
        }
        if (filters?.createdBy) {
          validations = validations.filter(v => (v as any).createdByDisplay === filters.createdBy);
        }

        // Sort by date descending
        validations.sort((a, b) => new Date(b.validationDate).getTime() - new Date(a.validationDate).getTime());

        // Client-side pagination
        const totalCount = validations.length;
        const totalPages = Math.ceil(totalCount / pageSize);
        const offset = (page - 1) * pageSize;
        const paginatedValidations = validations.slice(offset, offset + pageSize);

        return { data: paginatedValidations, totalCount, page, pageSize, totalPages };
      }

      // Admin user - use direct query (fetch all for time range, filter + paginate client-side)
      let query = supabase
        .from('inventory_validations')
        .select(`
          *,
          ingredient:ingredients(*)
        `)
        .eq('restaurant_id', restaurantId)
        .order('validation_date', { ascending: false });

      // Apply filters
      if (filters?.ingredientId) {
        query = query.eq('ingredient_id', filters.ingredientId);
      }
      if (dateRange.startDate) {
        query = query.gte('validation_date', dateRange.startDate.toISOString());
      }
      if (dateRange.endDate) {
        query = query.lte('validation_date', dateRange.endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching validations:', error);
        throw error;
      }

      // Get unique assistant IDs to fetch usernames
      const assistantIds = new Set<string>();
      (data || []).forEach((v: any) => {
        if (v.created_by_assistant_id) assistantIds.add(v.created_by_assistant_id);
        if (v.updated_by_assistant_id) assistantIds.add(v.updated_by_assistant_id);
      });

      // Fetch assistant usernames if there are any
      const assistantsMap = new Map<string, string>();
      if (assistantIds.size > 0) {
        const { data: assistants } = await supabase
          .from('assistants')
          .select('id, username')
          .in('id', Array.from(assistantIds));
        
        (assistants || []).forEach((a: any) => {
          assistantsMap.set(a.id, a.username);
        });
      }

      // Map snake_case database fields to camelCase TypeScript interface
      const validations = (data || []).map((v: any): InventoryValidation & { createdByDisplay?: string; updatedByDisplay?: string } => {
        const validation: InventoryValidation & { createdByDisplay?: string; updatedByDisplay?: string } = {
          id: v.id,
          restaurantId: v.restaurant_id,
          ingredientId: v.ingredient_id,
          inventoryId: v.inventory_id,
          validationDate: v.validation_date,
          calculatedQuantity: v.calculated_quantity,
          actualQuantity: v.actual_quantity,
          difference: v.difference ?? 0,
          unit: v.unit as MeasurementUnit,
          averageCostAtValidation: v.average_cost_at_validation,
          costVariance: v.cost_variance ?? 0,
          reason: v.reason ?? null,
          notes: v.notes ?? null,
          validatedBy: v.validated_by ?? null,
          createdAt: v.created_at || '',
          deletedAt: v.deleted_at || null,
          updatedAt: v.updated_at || null,
        };
        // Set created_by_display: show assistant username or 'Admin'
        if (v.created_by_assistant_id && assistantsMap.has(v.created_by_assistant_id)) {
          const username = assistantsMap.get(v.created_by_assistant_id);
          if (username) validation.createdByDisplay = username;
        } else if (v.validated_by) {
          validation.createdByDisplay = 'Admin';
        }
        // Set updated_by_display: show assistant username or 'Admin' (for deletions)
        if (v.updated_by_assistant_id && assistantsMap.has(v.updated_by_assistant_id)) {
          const username = assistantsMap.get(v.updated_by_assistant_id);
          if (username) validation.updatedByDisplay = username;
        } else if (v.updated_by || validation.deletedAt) {
          validation.updatedByDisplay = 'Admin';
        }
        if (v.ingredient) {
          validation.ingredient = {
            id: v.ingredient.id,
            restaurantId: v.ingredient.restaurant_id,
            nameAr: v.ingredient.name_ar,
            nameFr: v.ingredient.name_fr,
            emoji: v.ingredient.emoji || '',
            createdAt: v.ingredient.created_at || '',
            updatedAt: v.ingredient.updated_at || '',
          };
        }
        return validation;
      });

      // Filter by createdBy
      const filtered = filters?.createdBy
        ? validations.filter(v => (v as any).createdByDisplay === filters.createdBy)
        : validations;

      // Client-side pagination
      const totalCount = filtered.length;
      const totalPages = Math.ceil(totalCount / pageSize);
      const offset = (page - 1) * pageSize;
      const paginatedValidations = filtered.slice(offset, offset + pageSize);

      return {
        data: paginatedValidations,
        totalCount,
        page,
        pageSize,
        totalPages,
      };
    },
    enabled: !!restaurantId,
  });
}

/**
 * Validate inventory stock with physical count
 * Supports both admin users and assistants with appropriate permissions
 */
export function useValidateStock(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (validation: ValidateStockInput) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {

        // Use assistant RPC function that handles everything internally
        // Build params object with only defined values for optional fields
        const params: {
          p_restaurant_id: string;
          p_ingredient_id: string;
          p_actual_quantity: number;
          p_validation_date?: string;
          p_reason?: string;
          p_notes?: string;
        } = {
          p_restaurant_id: restaurantId,
          p_ingredient_id: validation.ingredientId,
          p_actual_quantity: validation.actualQuantity,
        };

        if (validation.validationDate) {
          params.p_validation_date = validation.validationDate.toISOString();
        }
        if (validation.reason) {
          params.p_reason = validation.reason;
        }
        if (validation.notes) {
          params.p_notes = validation.notes;
        }

        const { data, error } = await (supabase.rpc as any)('assistant_validate_inventory_stock', params);

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied');
          }
          console.error('Error validating stock:', error);
          throw error;
        }

        return data as string;
      }

      // Admin user - use direct RPC
      // Build params object with only defined values for optional fields
      const params: {
        p_restaurant_id: string;
        p_ingredient_id: string;
        p_actual_quantity: number;
        p_validation_date?: string;
        p_reason?: string;
        p_notes?: string;
      } = {
        p_restaurant_id: restaurantId,
        p_ingredient_id: validation.ingredientId,
        p_actual_quantity: validation.actualQuantity,
      };

      if (validation.validationDate) params.p_validation_date = validation.validationDate.toISOString();
      if (validation.reason) params.p_reason = validation.reason;
      if (validation.notes) params.p_notes = validation.notes;

      const { data, error } = await supabase.rpc('validate_inventory_stock', params);

      if (error) {
        console.error('Error validating stock:', error);
        throw error;
      }

      return data as string; // Returns validation_id
    },
    onSuccess: () => {
      // Invalidate all related queries using correct query keys
      queryClient.invalidateQueries({ queryKey: ['validations', restaurantId] });
      // Use queryKeys for inventory to match useInventory hook
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      // Invalidate menu item availability since inventory stock changed
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Stock validated successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to validate stock: ' + error.message);
    },
  });
}

// Input for deleting a validation
export interface DeleteValidationInput {
  validationId: string;
}

/**
 * Hook to delete a validation with inventory reversal
 */
export function useDeleteValidation(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeleteValidationInput) => {
      if (isAssistantLoggedIn()) {

        const { data, error } = await supabase.rpc('assistant_delete_inventory_validation' as any, {
          
          p_validation_id: input.validationId,
          p_restaurant_id: restaurantId,
        });

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied: You do not have permission to delete validations');
          }
          throw error;
        }

        return data;
      }

      // Admin user
      const { data, error } = await supabase.rpc('delete_inventory_validation' as any, {
        p_validation_id: input.validationId,
        p_restaurant_id: restaurantId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validations', restaurantId] });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.all() });
      queryClient.invalidateQueries({ queryKey: queryKeys.inventory.list({ restaurantId }) });
      queryClient.invalidateQueries({ queryKey: ['cost-analytics', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['inventory-insights'] });
      queryClient.invalidateQueries({ queryKey: ['menuItemAvailability', restaurantId] });
      queryClient.invalidateQueries({ queryKey: ['menuItemOptionAvailability'] });

      toast.success('Validation deleted successfully');
    },
    onError: (error: Error) => {
      toast.error('Failed to delete validation: ' + error.message);
    },
  });
}
